/**
 * CSV serialisation, RFC 4180.
 *
 * Pure and DOM-free on purpose — `download.ts` hands the string to the browser,
 * this file builds it. The split is what makes the quoting inspectable without a
 * browser, and quoting is the one thing here whose failure is invisible: a
 * `rationale` is prose a model wrote and routinely holds commas, "quoted phrases"
 * and newlines. An unquoted export does not error. It shifts every later column of
 * that one row under the wrong header, and the file still opens.
 *
 * Deliberately not the server-side `CSV_COLUMNS` in `deep_agent/report_tools.py`.
 * That is the harness writing its own artefact to container-local disk from the
 * ranking payload; this is a reader exporting the rows the read API returned. The
 * two answer to different sources, hold different columns, and are allowed to
 * differ — sharing one would make a UI export depend on a route that is mounted
 * only outside `LEADERBOARD_ONLY`.
 */

export interface CsvColumn<T> {
  /** Written into the header row, quoted by the same rules as any other field. */
  header: string
  /**
   * The cell for one row.
   *
   * `null` and `undefined` become an **empty** field — never `"null"` and never
   * `0`. An unmeasured axis is precisely why a target's coverage is below 100%, so
   * a literal 0 in a spreadsheet would contradict the coverage sitting next to it
   * while both columns looked populated.
   *
   * Anything that is not a scalar has to be flattened by the caller, so that the
   * choice of separator is made where the field's meaning is known rather than
   * here. `joinList` is the shared one.
   */
  value: (row: T) => string | number | boolean | null | undefined
}

/** Comma, double quote, CR or LF — RFC 4180 §2.6/§2.7's whole trigger list. */
const NEEDS_QUOTES = /[",\r\n]/

function field(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return ''
  const s = String(value)
  if (!NEEDS_QUOTES.test(s)) return s
  // A quote inside a quoted field is escaped by doubling it, not by a backslash:
  // a backslash is not an escape character in CSV and would survive into the cell.
  return `"${s.replace(/"/g, '""')}"`
}

/**
 * `rows` × `columns` as a CSV document, header row first.
 *
 * CRLF between records, per RFC 4180 §2.1, and not LF: a field may itself contain
 * a bare `\n` — the rationales do — so LF cannot also be the record separator
 * without a parser having to guess which of the two it is looking at.
 */
export function toCsv<T>(
  rows: readonly T[],
  columns: readonly CsvColumn<T>[],
): string {
  const lines = [columns.map((c) => field(c.header)).join(',')]
  for (const row of rows) {
    lines.push(columns.map((c) => field(c.value(row))).join(','))
  }
  return lines.join('\r\n') + '\r\n'
}

/**
 * A list of strings as one field, `; `-separated.
 *
 * Semicolon rather than comma only so the cell stays readable once quoted — the
 * quoting handles either. Empty for an empty or absent list, so it reads as
 * nothing recorded rather than as a literal `[]`.
 */
export function joinList(
  values: readonly (string | null | undefined)[] | null | undefined,
): string {
  if (!values) return ''
  return values.filter((v): v is string => !!v).join('; ')
}
